import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { colors } from '../theme/tokens';

export interface Column<T> {
    key: string;
    label?: React.ReactNode;
    align?: 'left' | 'right' | 'center';
    render: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
    columns: Column<T>[];
    rows: T[];
    rowKey: (row: T, index: number) => React.Key;
    emptyText?: string;
    onRowClick?: (row: T) => void;
    /* Page long lists: the initial rows per page (the pager offers 20 / 50 / 100 and shows only when a list is longer than the smallest option) */
    pageSize?: number;
}

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

const headSx = { fontSize: 11, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' as const, color: colors.textGrey, borderColor: colors.dividerBlue };

/* Plain list table: small uppercase headers, light dividers, row hover. Scrolls sideways on narrow screens. */
function DataTable<T>({ columns, rows, rowKey, emptyText = 'Nothing here yet', onRowClick, pageSize }: DataTableProps<T>) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize || ROWS_PER_PAGE_OPTIONS[0]);

    if (!rows.length) {
        return <Typography variant="body2" color="textSecondary">{emptyText}</Typography>;
    }

    const paged = pageSize ? rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : rows;
    const options = Array.from(new Set([...ROWS_PER_PAGE_OPTIONS, rowsPerPage])).sort((a, b) => a - b);

    return (
        <>
            <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.key} align={column.align} sx={headSx}>{column.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paged.map((row, i) => (
                            <TableRow
                                key={rowKey(row, i)}
                                hover
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                sx={{ cursor: onRowClick ? 'pointer' : 'default', '& td': { borderColor: '#f0f3f8', fontSize: 14 } }}
                            >
                                {columns.map((column) => (
                                    <TableCell key={column.key} align={column.align}>{column.render(row)}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            {pageSize && rows.length > options[0] && (
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={options}
                    labelRowsPerPage="Rows per page"
                    onPageChange={(_, next) => setPage(next)}
                    onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
                />
            )}
        </>
    );
}

export default DataTable;
